import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import {
  Play, StopCircle, Users, Lock, CheckCircle2, MapPin, Search,
  WifiOff, Wifi, CloudUpload, AlertTriangle, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import {
  enqueueAction, listQueued, flushQueue, registerAutoFlush, oldestQueueAgeMs, clearItem } from '../../lib/offlineQueue';
import { apiFetch } from '../../services/api';

const API = process.env.REACT_APP_BACKEND_URL;
const RUNNING_KEY = 'smartvolt_running_shift';

const initials = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const avatarColor = (id) => {
  const palette = ['bg-sky-500', 'bg-emerald-500', 'bg-purple-500', 'bg-orange-500', 'bg-pink-500', 'bg-cyan-500', 'bg-indigo-500', 'bg-teal-500'];
  const s = String(id || '');
  let sum = 0;
  for (let i = 0; i < s.length; i++) sum = (sum + s.charCodeAt(i)) % palette.length;
  return palette[sum];
};

/**
 * PWA-taugliche Zeiterfassung mit Offline-Queue.
 * - Online: direkter API-Call
 * - Offline: Aktion landet in IndexedDB, wird beim `online`-Event automatisch synchronisiert
 * - Läuft die Kolonne offline los, speichern wir den Client-Timestamp für den späteren Sync
 */
const TimeTrackingPanel = () => {
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [projectId, setProjectId] = useState('');
  const [note, setNote] = useState('');
  const [running, setRunning] = useState(null);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [elapsed, setElapsed] = useState('00:00:00');

  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [queueCount, setQueueCount] = useState(0);
  const [queueOldMs, setQueueOldMs] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const flushTimer = useRef(null);

  // -- Netz-Status + Queue-Metriken laden ------------------------------------
  const refreshQueueMetrics = useCallback(async () => {
    try {
      const items = await listQueued();
      setQueueCount(items.length);
      setQueueOldMs(await oldestQueueAgeMs());
    } catch {
      setQueueCount(0);
      setQueueOldMs(0);
    }
  }, []);

  // -- Fetch employees + projects on mount -----------------------------------
  useEffect(() => {
    (async () => {
      try {
        const [emp, proj] = await Promise.all([
          apiFetch(`${API}/api/pro/employees-lite`, { headers: {} }).then((r) => r.json()).catch(() => ({})),
          apiFetch(`${API}/api/pro/projects`, { headers: {} }).then((r) => r.json()).catch(() => ({})),
        ]);
        if (emp?.success) {
          setEmployees(emp.employees || []);
          const cu = emp.current_user_id;
          setCurrentUserId(cu);
          if (cu) setSelectedIds(new Set([cu]));
        }
        if (proj?.success) setProjects((proj.projects || []).filter((p) => p.status !== 'archived'));
      } catch (err) {
        // Offline beim Start → still weitermachen, running shift wird trotzdem restauriert
        console.warn('TimeTrackingPanel: Init-Fetch fehlgeschlagen (Offline?)', err);
      } finally {
        setLoading(false);
      }
    })();

    // Restore running session (überlebt App-Restarts)
    try {
      const raw = localStorage.getItem(RUNNING_KEY);
      if (raw) {
        const r = JSON.parse(raw);
        if (r?.started_at) {
          setRunning(r);
          setProjectId(r.project_id || '');
        }
      }
    } catch (err) {
      console.warn('TimeTrackingPanel: RUNNING_KEY konnte nicht deserialisiert werden', err);
    }

    refreshQueueMetrics();
  }, [refreshQueueMetrics]);

  // -- Elapsed-Timer --------------------------------------------------------
  useEffect(() => {
    if (!running) { setElapsed('00:00:00'); return; }
    const start = new Date(running.started_at).getTime();
    const tick = () => {
      const s = Math.max(0, Math.floor((Date.now() - start) / 1000));
      const h = String(Math.floor(s / 3600)).padStart(2, '0');
      const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
      const sec = String(s % 60).padStart(2, '0');
      setElapsed(`${h}:${m}:${sec}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [running]);

  // -- Sync-Handler (flush) --------------------------------------------------
  const doFlush = useCallback(async ({ silent = false } = {}) => {
    if (syncing) return;
    setSyncing(true);
    try {
      const endpointMap = {
        start: async (payload, ts) => {
          try {
            const res = await apiFetch(`${API}/api/pro/time/start`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json'},
              body: JSON.stringify({ ...payload, client_timestamp: ts }) });
            const data = await res.json();
            if (!res.ok || !data.success) return { ok: false, error: data.detail || 'Sync-Fehler' };
            // Wenn diese Session grade läuft, entry_ids nachtragen
            const cur = JSON.parse(localStorage.getItem(RUNNING_KEY) || 'null');
            if (cur && cur.pending_start && cur.pending_start_id === payload.__local_id) {
              const upd = { ...cur, entry_ids: data.entry_ids, pending_start: false, pending_start_id: undefined };
              localStorage.setItem(RUNNING_KEY, JSON.stringify(upd));
              setRunning(upd);
            }
            return { ok: true };
          } catch (err) {
            return { ok: false, error: err.message };
          }
        },
        stop: async (payload, ts) => {
          try {
            const res = await apiFetch(`${API}/api/pro/time/batch-stop`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json'},
              body: JSON.stringify({ entry_ids: payload.entry_ids, client_timestamp: ts }) });
            const data = await res.json();
            if (!res.ok || !data.success) return { ok: false, error: data.detail || 'Sync-Fehler' };
            return { ok: true };
          } catch (err) {
            return { ok: false, error: err.message };
          }
        },
        session: async (payload) => {
          try {
            const res = await apiFetch(`${API}/api/pro/time/offline-session`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json'},
              body: JSON.stringify(payload) });
            const data = await res.json();
            if (!res.ok || !data.success) return { ok: false, error: data.detail || 'Sync-Fehler' };
            return { ok: true };
          } catch (err) {
            return { ok: false, error: err.message };
          }
        } };
      const res = await flushQueue(endpointMap);
      await refreshQueueMetrics();
      if (!silent && (res.ok > 0 || res.failed > 0)) {
        if (res.failed === 0) {
          toast.success(`${res.ok} Zeit-Aktion(en) synchronisiert`);
        } else if (res.ok > 0) {
          toast.warning(`Nur ${res.ok} von ${res.ok + res.failed} synchronisiert`, {
            description: 'Manche Aktionen blieben in der Warteschlange.' });
        } else {
          toast.error(`${res.failed} Aktion(en) konnten nicht gesendet werden`);
        }
      }
    } finally {
      setSyncing(false);
    }
  }, [refreshQueueMetrics, syncing]);

  // -- Online/Offline Listener -----------------------------------------------
  useEffect(() => {
    const on = () => {
      setOnline(true);
      // kleiner Debounce, damit iOS/Android nicht sofort flackernd flushen
      clearTimeout(flushTimer.current);
      flushTimer.current = setTimeout(() => doFlush({ silent: false }), 800);
    };
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    const unregister = registerAutoFlush(() => doFlush({ silent: false }));
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
      unregister?.();
      clearTimeout(flushTimer.current);
    };
  }, [doFlush]);

  // -- Beim App-Start Queue direkt flushen, falls online ---------------------
  useEffect(() => {
    if (online) doFlush({ silent: true });
  }, [online, doFlush]);

  // Alle 30s Queue-Metriken aktualisieren (für Warn-Timer)
  useEffect(() => {
    const id = setInterval(refreshQueueMetrics, 30_000);
    return () => clearInterval(id);
  }, [refreshQueueMetrics]);

  const currentEmp = useMemo(() => employees.find((e) => e._id === currentUserId), [employees, currentUserId]);
  const filteredEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees.filter((e) => {
      if (e._id === currentUserId) return false;
      if (!q) return true;
      return (e.name || '').toLowerCase().includes(q) || (e.email || '').toLowerCase().includes(q);
    });
  }, [employees, currentUserId, search]);

  const toggle = (id) => {
    if (id === currentUserId) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // -- START -----------------------------------------------------------------
  const start = async () => {
    if (!projectId) { toast.error('Bitte Baustelle wählen'); return; }
    if (selectedIds.size === 0) { toast.error('Mindestens eine Person auswählen'); return; }
    setStarting(true);
    const clientTs = new Date().toISOString();
    const extraIds = Array.from(selectedIds).filter((id) => id !== currentUserId);
    const startPayload = { project_id: projectId, employee_ids: extraIds, note };

    // Online-Pfad
    if (navigator.onLine) {
      try {
        const res = await apiFetch(`${API}/api/pro/time/start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json'},
          body: JSON.stringify({ ...startPayload, client_timestamp: clientTs }) });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.detail || 'Fehler beim Start');
        const session = {
          entry_ids: data.entry_ids || [],
          started_at: clientTs,
          project_id: projectId,
          employee_ids: Array.from(selectedIds),
          pending_start: false };
        setRunning(session);
        localStorage.setItem(RUNNING_KEY, JSON.stringify(session));
        toast.success(`${data.entry_ids.length} Personen eingestempelt`, {
          description: `Baustelle: ${projects.find((p) => p._id === projectId)?.name || ''}` });
        return;
      } catch (err) {
        // Fällt in Offline-Pfad
        toast.info('Server nicht erreichbar – wechsle in Offline-Modus');
      }
    }

    // Offline-Pfad: enqueue + optimistisch UI updaten
    try {
      const localId = `local-${Date.now()}`;
      await enqueueAction('start', { ...startPayload, __local_id: localId });
      const session = {
        entry_ids: [], // wird beim Sync nachgetragen
        started_at: clientTs,
        project_id: projectId,
        employee_ids: Array.from(selectedIds),
        pending_start: true,
        pending_start_id: localId };
      setRunning(session);
      localStorage.setItem(RUNNING_KEY, JSON.stringify(session));
      await refreshQueueMetrics();
      toast.success(`${selectedIds.size} Personen offline eingestempelt`, {
        description: 'Wird automatisch synchronisiert, sobald Netz da ist.' });
    } catch (err) {
      toast.error('Konnte Aktion nicht speichern: ' + err.message);
    } finally {
      setStarting(false);
    }
  };

  // -- STOP ------------------------------------------------------------------
  const stop = async () => {
    if (!running) return;
    setStopping(true);
    const clientTs = new Date().toISOString();

    // Session ist noch pending_start (Server hat noch nie von ihr gehört).
    // Wir fassen Start + Stop in einer einzigen "session"-Queue-Aktion zusammen
    // und entfernen den zuvor gequeueten "start" damit keine Duplikate entstehen.
    if (running.pending_start || !running.entry_ids?.length) {
      try {
        // Alle "start"-Actions dieser lokalen Session löschen
        const all = await listQueued();
        const targetLocalId = running.pending_start_id;
        for (const item of all) {
          if (item.kind === 'start' && item.payload?.__local_id === targetLocalId) {
            await clearItem(item.id);
          }
        }
        await enqueueAction('session', {
          project_id: running.project_id,
          employee_ids: (running.employee_ids || []).filter((id) => id !== currentUserId),
          note: note || '',
          start_timestamp: running.started_at,
          end_timestamp: clientTs });
      } catch (err) {
        // Enqueue-Fehler dürfen den Stop-Flow nicht blockieren, aber wir loggen
        console.warn('TimeTrackingPanel: session-enqueue fehlgeschlagen', err);
      }
      setRunning(null);
      localStorage.removeItem(RUNNING_KEY);
      setNote('');
      setStopping(false);
      await refreshQueueMetrics();
      toast.success('Offline ausgestempelt – wird beim nächsten Netzkontakt gebucht');
      // Falls doch online, sofort synchronisieren
      if (navigator.onLine) doFlush({ silent: true });
      return;
    }

    // Online-Pfad
    if (navigator.onLine) {
      try {
        const res = await apiFetch(`${API}/api/pro/time/batch-stop`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json'},
          body: JSON.stringify({ entry_ids: running.entry_ids, client_timestamp: clientTs }) });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.detail || 'Fehler');
        const hrs = (data.total_minutes / 60).toFixed(2);
        toast.success(`${data.stopped} Buchungen gestoppt`, { description: `Insgesamt ${hrs} Std. gebucht` });
        setRunning(null);
        localStorage.removeItem(RUNNING_KEY);
        setNote('');
        return;
      } catch (err) {
        toast.info('Server nicht erreichbar – speichere Ausstempeln offline');
      }
    }

    // Offline-Pfad
    try {
      await enqueueAction('stop', { entry_ids: running.entry_ids });
      setRunning(null);
      localStorage.removeItem(RUNNING_KEY);
      setNote('');
      toast.success('Offline ausgestempelt', { description: 'Wird beim nächsten Netzkontakt gebucht.' });
      await refreshQueueMetrics();
    } catch (err) {
      toast.error('Konnte Ausstempeln nicht speichern: ' + err.message);
    } finally {
      setStopping(false);
    }
  };

  if (loading) return <Card className="bg-gray-900/60 border-gray-800"><CardContent className="p-6 text-center text-gray-500">Lade Kolonne…</CardContent></Card>;

  const queueOldMinutes = Math.floor(queueOldMs / 60000);
  const queueTooOld = queueOldMinutes >= 60;

  return (
    <div className="space-y-4" data-testid="monteur-time-panel">
      {/* NETZ-STATUS BADGE */}
      <div className="flex items-center justify-between gap-2" data-testid="net-status-bar">
        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
          online
            ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
            : 'bg-amber-500/15 text-amber-300 border border-amber-500/40 animate-pulse'
        }`} data-testid="net-status-badge">
          {online ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
          {online ? 'Online' : 'Offline-Modus'}
        </div>
        {queueCount > 0 && (
          <button
            type="button"
            onClick={() => doFlush({ silent: false })}
            disabled={!online || syncing}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
              queueTooOld
                ? 'bg-rose-500/15 text-rose-300 border-rose-500/40 hover:bg-rose-500/25'
                : 'bg-sky-500/15 text-sky-300 border-sky-500/30 hover:bg-sky-500/25'
            } disabled:opacity-50`}
            data-testid="queue-sync-btn"
            title="Manuell synchronisieren"
          >
            {syncing ? <RefreshCcw className="h-3.5 w-3.5 animate-spin" /> : <CloudUpload className="h-3.5 w-3.5" />}
            {queueCount} wartet
          </button>
        )}
      </div>

      {/* WARN-BANNER: Queue älter als 1 Stunde */}
      {queueTooOld && (
        <div className="flex items-start gap-2 p-3 rounded-xl border border-rose-500/40 bg-rose-500/10" data-testid="queue-stale-warning">
          <AlertTriangle className="h-4 w-4 text-rose-300 mt-0.5 shrink-0" />
          <div className="text-xs text-rose-200 leading-relaxed">
            <p className="font-semibold">Achtung: Nicht synchronisierte Zeiten seit {queueOldMinutes >= 60 ? `${Math.floor(queueOldMinutes/60)} Std ${queueOldMinutes%60} Min` : `${queueOldMinutes} Min`}</p>
            <p className="text-rose-300/80 mt-0.5">Verbinde dich mit dem Netz oder bitte den Chef, die Zeit im Dashboard manuell nachzutragen.</p>
          </div>
        </div>
      )}

      {/* LAUFENDE BUCHUNG */}
      {running ? (
        <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border-2 border-emerald-500/50" data-testid="running-shift-card">
          <CardContent className="p-5 text-center space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/40">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              <span className="text-xs font-semibold text-emerald-300 uppercase tracking-wider">Kolonne läuft{running.pending_start ? ' · pending sync' : ''}</span>
            </div>
            <div>
              <p className="text-5xl font-black text-white font-mono tabular-nums tracking-tight" data-testid="running-elapsed">{elapsed}</p>
              <p className="text-xs text-gray-400 mt-1">{projects.find((p) => p._id === running.project_id)?.name || 'Baustelle'}</p>
            </div>
            <div className="flex flex-wrap justify-center gap-1.5">
              {(running.employee_ids || []).map((id) => {
                const e = employees.find((x) => x._id === id);
                return (
                  <span key={id} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs bg-emerald-500/15 border border-emerald-500/30">
                    <span className={`w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center ${avatarColor(id)}`}>{initials(e?.name)}</span>
                    <span className="text-emerald-200">{e?.name || 'Monteur'}</span>
                  </span>
                );
              })}
            </div>
            <Button
              onClick={stop}
              disabled={stopping}
              className="w-full bg-rose-500 hover:bg-rose-400 text-white font-bold py-7 text-lg rounded-2xl shadow-lg shadow-rose-500/30"
              data-testid="monteur-time-stop"
            >
              <StopCircle className="h-6 w-6 mr-2" />
              {stopping ? 'Stoppe…' : 'Kolonne ausstempeln'}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* PROJEKT-DROPDOWN */}
          <Card className="bg-gray-900/60 border-gray-800">
            <CardContent className="p-4 space-y-3">
              <Label className="text-white font-semibold flex items-center gap-2">
                <MapPin className="h-4 w-4 text-yellow-400" /> Baustelle wählen
              </Label>
              <select
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                className="w-full bg-gray-800 border-2 border-gray-700 focus:border-yellow-500 rounded-xl px-4 py-4 text-white text-base appearance-none"
                data-testid="monteur-project-select"
              >
                <option value="">— Bitte Baustelle wählen —</option>
                {projects.map((p) => (
                  <option key={p._id} value={p._id}>{p.name}{p.address ? ` · ${p.address}` : ''}</option>
                ))}
              </select>
              {projects.length === 0 && (
                <p className="text-xs text-gray-500">
                  {online
                    ? 'Der Chef hat noch keine Baustellen angelegt.'
                    : 'Offline — keine Baustellen im Cache. Bitte einmal mit Netz öffnen.'}
                </p>
              )}
            </CardContent>
          </Card>

          {/* KOLONNE (CHIP-PICKER) */}
          <Card className="bg-gray-900/60 border-gray-800">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-white font-semibold flex items-center gap-2">
                  <Users className="h-4 w-4 text-yellow-400" /> Kolonne
                </Label>
                <span className="text-xs text-gray-400" data-testid="colonne-count">
                  {selectedIds.size} {selectedIds.size === 1 ? 'Person' : 'Personen'}
                </span>
              </div>

              {currentEmp && (
                <div className="flex items-center gap-3 p-3 bg-yellow-500/15 border-2 border-yellow-500/50 rounded-2xl" data-testid="chip-self">
                  <span className={`w-11 h-11 rounded-full text-white text-sm font-bold flex items-center justify-center ${avatarColor(currentUserId)}`}>
                    {initials(currentEmp.name)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold truncate">{currentEmp.name}</p>
                    <p className="text-xs text-yellow-400 flex items-center gap-1"><Lock className="h-3 w-3" /> Du · immer dabei</p>
                  </div>
                  <CheckCircle2 className="h-6 w-6 text-yellow-400" />
                </div>
              )}
              {!currentEmp && currentUserId && employees.some((e) => e._id) && (
                <p className="text-xs text-gray-500 italic">Du bist als Chef eingeloggt – für Chef-Testbuchungen wähle Kollegen aus der Liste dazu.</p>
              )}

              {employees.length > 5 && (
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Kollege suchen…"
                    className="pl-9 bg-gray-800 border-gray-700 h-11"
                    data-testid="colonne-search"
                  />
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-1" data-testid="colonne-chip-list">
                {filteredEmployees.length === 0 ? (
                  <p className="text-xs text-gray-500 py-2 w-full text-center">
                    {employees.length <= 1 ? 'Noch keine weiteren Kollegen im Team.' : 'Keine Treffer.'}
                  </p>
                ) : (
                  filteredEmployees.map((e) => {
                    const active = selectedIds.has(e._id);
                    return (
                      <button
                        key={e._id}
                        type="button"
                        onClick={() => toggle(e._id)}
                        className={`inline-flex items-center gap-2 pl-1 pr-3 py-1.5 rounded-full transition-all active:scale-95 min-h-[44px] ${
                          active
                            ? 'bg-yellow-500 text-black font-semibold shadow-lg shadow-yellow-500/30 ring-2 ring-yellow-300'
                            : 'bg-gray-800 text-gray-200 border border-gray-700 hover:bg-gray-700'
                        }`}
                        data-testid={`chip-${e._id}`}
                        aria-pressed={active}
                      >
                        <span className={`w-8 h-8 rounded-full text-white text-xs font-bold flex items-center justify-center ${avatarColor(e._id)}`}>
                          {initials(e.name)}
                        </span>
                        <span className="text-sm">{e.name}</span>
                        {active && <CheckCircle2 className="h-4 w-4 text-black" />}
                      </button>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>

          {/* NOTIZ */}
          <Card className="bg-gray-900/60 border-gray-800">
            <CardContent className="p-4 space-y-2">
              <Label className="text-white text-sm">Notiz (optional)</Label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="z.B. Rohinstallation UG"
                className="bg-gray-800 border-gray-700 h-11"
                data-testid="monteur-time-note"
              />
            </CardContent>
          </Card>

          {/* START */}
          <Button
            onClick={start}
            disabled={starting || !projectId || selectedIds.size === 0}
            className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 text-black font-bold py-7 text-lg rounded-2xl shadow-lg shadow-yellow-500/30 disabled:opacity-40 disabled:shadow-none"
            data-testid="monteur-time-start"
          >
            <Play className="h-6 w-6 mr-2" />
            {starting ? 'Starte…' : `Kolonne einstempeln (${selectedIds.size})`}
          </Button>
        </>
      )}
    </div>
  );
};

export default TimeTrackingPanel;
